import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const today = () => new Date().toISOString().split("T")[0];

const trustPoints = [
    "One person, start to finish — the owner does every valet",
    "Price agreed before any work begins",
    "Dates confirmed personally by phone or email",
];

export const BookingSection = () => {
    const [services, setServices] = useState([]);
    const [form, setForm] = useState({ name: "", phone: "", email: "", service_id: "", preferred_date: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/services").then((res) => setServices(res.data)).catch(() => {});
    }, []);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.service_id) {
            toast.error("Please choose a service");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/bookings", { ...form, email: form.email || null, notes: form.notes || null });
            setDone(true);
            toast.success("Booking request sent — I'll be in touch shortly to confirm.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const fieldCls = "bg-indigo-900/60 border-indigo-400/30 text-white h-12 rounded-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-950 placeholder:text-blue-200/40";

    return (
        <section id="book" className="relative py-24 sm:py-32 bg-indigo-950 overflow-hidden pv-noise" data-testid="booking-section">
            <div className="absolute inset-0 pv-stars opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9 }}
                        className="lg:col-span-5"
                    >
                        <p className="text-3xl sm:text-4xl tracking-[0.2em] uppercase font-semibold text-white mb-8" data-testid="booking-eyebrow">Book a valet</p>
                        <p className="text-lg sm:text-xl leading-relaxed font-medium text-white mb-10" data-testid="booking-heading">
                            Select one of the standard services or tell me what you need in the contact form opposite.
                        </p>
                        <div className="border-l-2 border-blue-300/50 pl-6 mb-10">
                            <Quote className="w-5 h-5 text-blue-300/60 mb-4" />
                            <p className="font-serif-display italic text-xl sm:text-2xl text-white leading-relaxed mb-4" data-testid="booking-owner-quote">
                                "For over 20 years, most of my work has come from people telling people. I'd like to keep it that way — so every job has to be my best."
                            </p>
                            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white">The owner, Planet Valet</p>
                        </div>
                        <ul className="space-y-4" data-testid="booking-trust-list">
                            {trustPoints.map((t, i) => (
                                <li key={i} className="flex items-start gap-3 text-base sm:text-lg font-medium text-white">
                                    <CheckCircle2 className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" /> {t}
                                </li>
                            ))}
                        </ul>
                        <a href="tel:+447481776151" className="inline-flex items-center gap-4 mt-10 border border-indigo-400/25 bg-indigo-900/40 px-6 py-4 hover:border-blue-300/50 transition-colors duration-300" data-testid="booking-call-link">
                            <Phone className="w-5 h-5 text-blue-300 shrink-0" />
                            <span>
                                <span className="block text-xs tracking-[0.2em] uppercase font-semibold text-white">Prefer to talk?</span>
                                <span className="block font-serif-display text-xl text-white">Call Simon on 07481 776151</span>
                            </span>
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.15 }}
                        className="lg:col-span-6 lg:col-start-7"
                    >
                        {done ? (
                            <div className="border border-blue-300/40 bg-indigo-900/40 p-10 sm:p-16 text-center" data-testid="booking-success-panel">
                                <CheckCircle2 className="w-12 h-12 text-blue-300 mx-auto mb-6" />
                                <h3 className="font-serif-display text-3xl text-white mb-4">Request received.</h3>
                                <p className="text-white font-medium leading-relaxed mb-8">
                                    Thank you — your booking request is in. I'll ring or email you personally to confirm the date and final price.
                                </p>
                                <button
                                    onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", service_id: "", preferred_date: "", notes: "" }); }}
                                    className="text-xs tracking-[0.2em] uppercase font-bold text-blue-300 hover:text-white transition-colors"
                                    data-testid="booking-another-button"
                                >
                                    Make another booking
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="border border-indigo-400/25 bg-indigo-900/40 p-8 sm:p-12 space-y-6" data-testid="booking-form">
                                <div className="space-y-2">
                                    <Label htmlFor="bk-name" className="text-xs tracking-[0.2em] uppercase font-semibold text-white">Your name *</Label>
                                    <Input id="bk-name" required value={form.name} onChange={set("name")} className={fieldCls} data-testid="booking-name-input" />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="bk-phone" className="text-xs tracking-[0.2em] uppercase font-semibold text-white">Phone *</Label>
                                        <Input id="bk-phone" required type="tel" value={form.phone} onChange={set("phone")} className={fieldCls} data-testid="booking-phone-input" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bk-email" className="text-xs tracking-[0.2em] uppercase font-semibold text-white">Email</Label>
                                        <Input id="bk-email" type="email" value={form.email} onChange={set("email")} className={fieldCls} data-testid="booking-email-input" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-white">Service *</Label>
                                        <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
                                            <SelectTrigger className={`${fieldCls} w-full`} data-testid="booking-service-select">
                                                <SelectValue placeholder="Choose a service" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-indigo-900 border-indigo-400/25 text-white rounded-none">
                                                {services.map((s) => (
                                                    <SelectItem key={s.id} value={s.id} data-testid={`booking-service-option-${s.id}`}>
                                                        {s.name} · {s.price}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bk-date" className="text-xs tracking-[0.2em] uppercase font-semibold text-white">Preferred date *</Label>
                                        <Input id="bk-date" required type="date" min={today()} value={form.preferred_date} onChange={set("preferred_date")} className={fieldCls} data-testid="booking-date-input" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="bk-notes" className="text-xs tracking-[0.2em] uppercase font-semibold text-white">Any special requests</Label>
                                    <Textarea id="bk-notes" rows={4} value={form.notes} onChange={set("notes")} className={`${fieldCls} h-auto`} data-testid="booking-notes-input" />
                                </div>
                                <motion.button
                                    whileTap={{ scale: 0.98 }}
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-white hover:bg-blue-50 disabled:opacity-60 text-indigo-950 text-sm tracking-[0.15em] uppercase font-bold py-4 transition-colors duration-300"
                                    data-testid="booking-submit-button"
                                >
                                    {submitting ? "Sending…" : "Request booking"}
                                </motion.button>
                                <p className="text-sm text-white font-normal text-center">No payment now — this simply sends me your request.</p>
                            </form>
                        )}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
